import type { WatchPart } from '../api/client'

export type PurchaseLinkSource = 'part_card' | 'build_summary' | 'buy_full_build'

const PROVIDER_LABELS: Record<string, string> = {
  lucius_atelier: 'Lucius Atelier',
  namoki_mods: 'namokiMODS',
}

function outboundOrigin(): string {
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin
  }
  return ''
}

function buildQuery(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      search.set(key, value)
    }
  }
  const query = search.toString()
  return query ? `?${query}` : ''
}

export function providerDisplayName(providerId: string): string {
  return PROVIDER_LABELS[providerId] ?? providerId.replace(/_/g, ' ')
}

export function partHasPurchaseLink(part: WatchPart): boolean {
  return Boolean(part.purchase_url || part.metadata?.source_url)
}

export function variantIdForPart(part: WatchPart): string | null {
  const variantId =
    part.metadata?.selected_variant_id ??
    part.metadata?.default_variant_id ??
    part.metadata?.variant_id
  if (variantId == null) return null
  return String(variantId)
}

export function canBuyFullBuild(parts: WatchPart[]): boolean {
  if (parts.length < 2) return false

  const providerIds = new Set(parts.map((part) => part.provider_id))
  if (providerIds.size !== 1) return false

  return parts.every((part) => partHasPurchaseLink(part) && variantIdForPart(part))
}

export function buildPartPurchaseUrl({
  partId,
  source,
  conversationId,
  buildPartIds,
  variantId,
}: {
  partId: string
  source: PurchaseLinkSource
  conversationId?: string
  buildPartIds?: string[]
  variantId?: string | null
}): string {
  return `${outboundOrigin()}/go/part${buildQuery({
    part_id: partId,
    source,
    conversation_id: conversationId,
    build_part_ids: buildPartIds?.join(','),
    variant_id: variantId ?? undefined,
  })}`
}

export function buildCartPurchaseUrl({
  partIds,
  conversationId,
  variantIds,
}: {
  partIds: string[]
  conversationId?: string
  variantIds?: Array<string | null>
}): string {
  const resolvedVariantIds = variantIds?.map((variantId) => variantId ?? undefined).filter(Boolean)
  return `${outboundOrigin()}/go/build${buildQuery({
    part_ids: partIds.join(','),
    source: 'buy_full_build',
    conversation_id: conversationId,
    variant_ids: resolvedVariantIds && resolvedVariantIds.length > 0 ? resolvedVariantIds.join(',') : undefined,
  })}`
}
